import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import apiClient from '@/services/api/client';

export const ATTACHMENT_CATEGORY_OPTIONS = [
  { value: 'insurance', labelKey: 'vehicleAttachments.insurance' },
  { value: 'inspection', labelKey: 'vehicleAttachments.inspection' },
  { value: 'damage', labelKey: 'vehicleAttachments.damage' },
  { value: 'invoice', labelKey: 'vehicleAttachments.invoice' },
  { value: 'contract', labelKey: 'vehicleAttachments.contract' },
  { value: 'service', labelKey: 'vehicleAttachments.service' },
  { value: 'other', labelKey: 'vehicleAttachments.other' },
] as const;

export interface CustomAttachmentCategory {
  code: string;
  name: string;
}

const ADD_VALUE = '__add__';

export function attachmentCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    insurance: 'bg-blue-100 text-blue-800',
    inspection: 'bg-green-100 text-green-800',
    damage: 'bg-red-100 text-red-800',
    invoice: 'bg-yellow-100 text-yellow-800',
    contract: 'bg-purple-100 text-purple-800',
    service: 'bg-orange-100 text-orange-800',
    other: 'bg-gray-100 text-gray-800',
  };
  if (category.startsWith('c:')) return 'bg-teal-100 text-teal-800';
  return colors[category] || colors.other;
}

export function attachmentCategoryLabel(
  category: string,
  t: (key: string) => string,
  custom: CustomAttachmentCategory[],
  categoryName?: string | null
): string {
  const builtin = ATTACHMENT_CATEGORY_OPTIONS.find((opt) => opt.value === category);
  if (builtin) return t(builtin.labelKey);
  if (categoryName) return categoryName;
  return custom.find((item) => item.code === category)?.name || category;
}

export function useVehicleAttachmentCategories(vehicleId: number): {
  categories: CustomAttachmentCategory[];
  addCategory: (category: CustomAttachmentCategory) => void;
} {
  const [categories, setCategories] = useState<CustomAttachmentCategory[]>([]);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get(`/api/vehicles/${vehicleId}/attachment-categories`)
      .then((response) => {
        if (!cancelled && response.data?.success) {
          setCategories(response.data.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [vehicleId]);

  const addCategory = useCallback((category: CustomAttachmentCategory) => {
    setCategories((prev) => {
      if (prev.some((item) => item.code === category.code)) return prev;
      return [...prev, category].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    });
  }, []);

  return { categories, addCategory };
}

interface VehicleAttachmentCategoryFieldProps {
  vehicleId: number;
  value: string;
  categories: CustomAttachmentCategory[];
  onChange: (value: string) => void;
  onCreated: (category: CustomAttachmentCategory) => void;
}

export const VehicleAttachmentCategoryField: React.FC<VehicleAttachmentCategoryFieldProps> = ({
  vehicleId,
  value,
  categories,
  onChange,
  onCreated,
}) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      setSaving(true);
      setError(null);
      const response = await apiClient.post(`/api/vehicles/${vehicleId}/attachment-categories`, {
        name: trimmed,
      });
      if (response.data?.success && response.data.data?.code) {
        const created: CustomAttachmentCategory = {
          code: response.data.data.code,
          name: response.data.data.name,
        };
        onCreated(created);
        onChange(created.code);
        setName('');
        setAdding(false);
      } else {
        setError(response.data?.error || t('vehicleAttachments.categorySaveFailed'));
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(message || t('vehicleAttachments.categorySaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t('vehicleAttachments.category')}
      </label>
      <select
        value={value}
        onChange={(e) => {
          if (e.target.value === ADD_VALUE) {
            setAdding(true);
            setError(null);
            return;
          }
          onChange(e.target.value);
        }}
        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
      >
        {ATTACHMENT_CATEGORY_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {t(opt.labelKey)}
          </option>
        ))}
        {categories.map((category) => (
          <option key={category.code} value={category.code}>
            {category.name}
          </option>
        ))}
        <option value={ADD_VALUE}>{t('vehicleAttachments.addCategory')}</option>
      </select>
      {adding && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-textSecondary">{t('vehicleAttachments.categoryCustomerHint')}</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void save();
                }
              }}
              placeholder={t('vehicleAttachments.newCategoryPlaceholder')}
              className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
            />
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || !name.trim()}
              className="px-3 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
            >
              {t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setName('');
                setError(null);
              }}
              className="px-3 py-2 border border-border rounded-lg"
            >
              {t('common.cancel')}
            </button>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
};
